import React from 'react';
import { actionColumnDefinition, booleanColumnDefinition, ColumnDefinition, currencyColumnDefinition, numericColumnDefinition, textColumnDefinition } from '../table/ColumnDefinitionNew';
import { ITransaction } from '../../requests/ITransactionWebModelRequest';
import { UtilService } from '../../services/UtilService';
import { WebCache } from '../../services/WebCache';
import { TFunction } from 'react-i18next';
import { ReactComponent as TicketIMG } from 'itd-common/assets/images/transactions/Receipt Slip.svg';
import { ReactComponent as IconPlus } from 'itd-common/assets/icons/icon-plus.svg';
import TarjetaLogo from 'itd-common/assets/images/issuers/PCT-logo.svg';
import i18next from 'i18next';
import { TransactionService } from '../../services/TransactionService';
import { CurrencyHelper } from '../../helpers/CurrencyHelper';
import './TransactionReport.scss'

interface TransactionColumnsProps {
    t: TFunction;
    processAction?: (event: any, transaction: ITransaction) => void;
    showTicket?: (tranId: string, transactionType: string) => void;
    showMoreDetails: (transaction: ITransaction) => void;
    showCouponColumn?: boolean;
    setRowDetail?: (rowDetail: RowDetailType | null) => void;
}

type BuildTotalDetailOptions = { object: any, title: string, accumulateTotal: boolean };

interface RowDetailType {
    title: string;
    total: string;
    data: Array<{ prop: string; value: string }>;
}

export const TransactionColumns = ({ t, processAction, showMoreDetails, showCouponColumn, showTicket, setRowDetail }: TransactionColumnsProps): ColumnDefinition[] => {
    const T_PREFIX = 'pages.reportTransactions.';

    const showTotalRefund = (rowObject: ITransaction, detailOptions: BuildTotalDetailOptions): void => {
        if (detailOptions && setRowDetail) {
            const currencySymbol: string = CurrencyHelper.getCurrencySymbol(rowObject.currency);
            setRowDetail(TransactionService.buildTotalDetail(detailOptions.object, detailOptions.title, currencySymbol, detailOptions.accumulateTotal));
        }
    };

    const showTotalRefundAction = (rowObject: ITransaction): void => {
        var o: any = {};
        let label = i18next.t('relatedTransactions');
        let accumulateTotal = true;
        if (rowObject.transactionType == "D") {
            label = i18next.t('relatedSale');
            accumulateTotal = false;
        }
        for (var i in rowObject.relatedTransactions) {
            o[rowObject.relatedTransactions[i].transactionReference] = Number.parseInt(rowObject.relatedTransactions[i].amount) / 100;
        }
        showTotalRefund(rowObject, { object: o, title: label, accumulateTotal: accumulateTotal });
    };

    const getCategoryTranslation = (key: string) => {
        return t(`${T_PREFIX}category.${key}`);
    }

    var couponTransactionTypes = WebCache.isUserUYU() ? ['C', 'A', 'L', 'D', 'T'] : ['C', 'A', 'L', 'D'];
    return [
        WebCache.getCurrentUser()?.countryId == 1 ? actionColumnDefinition({
            key: 'relatedTransactions', label: t(`${T_PREFIX}transactionsColumn.transactionsVinculated`), category: getCategoryTranslation("transactionDetail"), header: false, action: {
                function: showTotalRefundAction.bind(this),
                icon: 'fa-solid fa-magnifying-glass'
            }
        }) :
            textColumnDefinition({ key: '', label: '' }),
        showCouponColumn
            ? actionColumnDefinition({
                key: 'coupon',
                label: "",
                header: true,
                action: {
                    function: (rowObject: ITransaction) => {
                        if (
                            showTicket &&
                            rowObject.acquirer !== '0700' &&
                            couponTransactionTypes.includes(rowObject.transactionType) &&
                            ['00', '08', '11', '85', 'OF'].includes(rowObject.responseCode)
                        ) {
                            return showTicket(rowObject.id, rowObject.transactionType);
                        } else {
                            return <TicketIMG color="gray" />;
                        }
                    },
                    icon: (rowObject: ITransaction) => {
                        if (
                            showTicket &&
                            rowObject.acquirer !== '0700' &&
                            couponTransactionTypes.includes(rowObject.transactionType) &&
                            ['00', '08', '11', '85', 'OF'].includes(rowObject.responseCode)
                        ) {
                            return <TicketIMG color='black' />;
                        } else {
                            return <TicketIMG color="gray" />;
                        }
                    },
                },
            })
            : textColumnDefinition({ key: '', label: '' }),
        actionColumnDefinition({
            key: 'show',
            label: "",
            header: true,
            action: {
                function: (rowObject: ITransaction) => {
                    // Mostrar detalle de transaccion
                    showMoreDetails(rowObject);
                },
                icon: (rowObject: ITransaction) => {
                    return <IconPlus />
                },
            },
        }),
        textColumnDefinition({
            key: 'authDateTime', label: t(`${T_PREFIX}transactionsColumn.authDateTime`), category: getCategoryTranslation("transactionDetail"), sortable: true, header: true, mapValue: (rowObject: ITransaction) => {
                const authDate = rowObject.authDateTime
                return UtilService.getDDMMyyHHmmssFormatFromString(authDate.toString())
            }
        }),
        textColumnDefinition({
            key: 'terminal', label: t(`${T_PREFIX}transactionsColumn.terminal`), category: getCategoryTranslation("transactionDetail"), header: true, mapValue: (rowObject: ITransaction) => {
                const terminal = rowObject.terminal
                if (terminal.toString().includes("B24_")) {
                    return terminal ? t(terminal).substring(4) : "-"
                } else {
                    return terminal ? t(terminal) : "-"
                }
            }
        }),
        textColumnDefinition({
            key: 'transactionType', label: t(`${T_PREFIX}transactionsColumn.transactionType`), category: getCategoryTranslation("transactionDetail"), filterKind: 'select', header: true, mapValue: (rowObjects: ITransaction) => {
                const transactionType = rowObjects.transactionType
                return transactionType ? t("transactionType" + "-" + transactionType) : "-"
            }
        }),
        textColumnDefinition({
            key: 'inputMode', label: t(`${T_PREFIX}transactionsColumn.inputMode`), filterKind: "image", category: getCategoryTranslation("transactionDetail"), header: true, mapValue: (rowObjects: ITransaction) => {
                const inputMode = rowObjects.inputMode
                return ( 
                    inputMode? <span>{t("inputMode" + "-" + inputMode)}</span>: "-"
                    )
                }
        }),
        textColumnDefinition({
            key: 'issuer',
            label: t(`${T_PREFIX}transactionsColumn.issuer`),
            category: getCategoryTranslation("transactionDetail"),
            header: true,
            filterKind: 'image',
            mapValue: (rowObject: ITransaction) => {
                const issuer = rowObject.issuer;
                const { value: displayValue, image: imageSrc } = UtilService.getIssuerGridValueInt(issuer, "issuer", t);
                if (rowObject.inputMode === "QRFD") {
                    return <img src={TarjetaLogo} alt={'PCT'} />;
                } else if (rowObject.issuer === "VISA-N/A-ARV7VISA") {
                    return <img src={imageSrc} alt={'VISA'} />;
                } else {
                    return (
                        issuer ?
                            <div className="d-flex">
                                <span className="pe-1 d-flex align-items-center">{displayValue}</span>
                                {imageSrc && <img src={imageSrc} width="50px" alt={displayValue} />}
                            </div> : "-"
                    );
                }
            },
        }),
        textColumnDefinition({
            key: 'acquirer', label: t(`${T_PREFIX}transactionsColumn.acquirer`), category: getCategoryTranslation("paymentInfo"), filterKind: 'image', header: true, mapValue: (rowObject: ITransaction) => {
                const acquirer = rowObject.acquirer;
                if (acquirer === "0000") {
                    return "-";
                } else {
                    let value = t("acquirer-" + acquirer).toUpperCase();
                    let image = UtilService.getImageTag(value);

                    if (value.indexOf("EDENRED") >= 0) {
                        value = " " + value.replace("EDENRED", "").trim().toLowerCase();
                    } else if (value.indexOf("CABAL") >= 0) {
                        value = " " + value.replace("CABAL", "").trim().toLowerCase();
                    } else if (value.indexOf("CREDITEL") >= 0) {
                        value = " " + value.replace("CREDITEL", "").trim().toLowerCase();
                    } else if (value.indexOf("PASS CARD") >= 0) {
                        value = " " + value.replace("PASS CARD", "").trim().toLowerCase();
                    } else {
                        value = "";
                    }
                    return (
                        <div>
                            <img width="50px" src={image} alt={`${acquirer}`} />
                        </div>
                    );
                }
            }
        }),
        textColumnDefinition({
            key: 'state', label: t(`${T_PREFIX}transactionsColumn.state`), category: getCategoryTranslation("transactionDetail"), filterKind: 'image', header: true, mapValue: (rowObjects: ITransaction) => {
                let state = rowObjects.state ? Number(rowObjects.state) : rowObjects.state;
                let stateColor = "st-chip ";
                if (state === 1) {
                    stateColor += "st-chip-state-1";
                } else if ((state >= 2 && state <= 4) || (state >= 13 && state <= 16)) {
                    stateColor += "st-chip-state-2";
                    state = 2;//con esto simplificamos el filtrado en el smartTable
                } else if (state === 5) {
                    stateColor += "st-chip-state-5";
                } else if (state === 6) {
                    stateColor += "st-chip-state-6";
                } else if ((state >= 7 && state <= 9)) {
                    stateColor += "st-chip-state-7";
                } else if (state === 10) {
                    stateColor += "st-chip-state-10";
                } else if (state === 11) {
                    stateColor += "st-chip-state-11";
                } else if (state === 12) {
                    stateColor += "st-chip-state-12";
                } else if (state === 22) {
                    stateColor += "st-chip-state-22";
                } else if (state === 25) {
                    stateColor += "st-chip-state-25";
                } else {
                    stateColor += "st-chip--default";
                }
                return state ? <div><span className={stateColor}>{t("state-" + state)}</span></div> : "-"
            }
        }),

        WebCache.isUserARG() ?
            textColumnDefinition({
                key: 'walletProductId', label: t(`${T_PREFIX}transactionsColumn.walletProductId`), filterKind: 'select', category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                    const wallet = rowObject.walletProductId;
                    if (!wallet) return "-";
                    const label = t("issuer" + "-" + wallet);
                    const imageSrc = UtilService.getImageTag(label);
                    return imageSrc ? <img width="50px" src={imageSrc} alt={label} /> : <span>{label}</span>;
                }
            }) :
            textColumnDefinition({ key: '', label: '' }),
        textColumnDefinition({
            key: 'responseCode', label: t(`${T_PREFIX}transactionsColumn.responseCode`), header: true, category: getCategoryTranslation("transactionDetail"), filterKind: 'image', mapValue: (rowObject: ITransaction) => {
                const responseCode = rowObject.responseCode
                if (responseCode === "00" || responseCode === "08" || responseCode === "11" || responseCode === "85" || responseCode === "LV" || responseCode === "OF") {
                    return <div className='response-code-success'>{t("responseCode" + "-" + responseCode)}</div>
                } else if (responseCode === "01" || responseCode === "08" || responseCode === "11" || responseCode === "85" || responseCode === "LV" || responseCode === "OF") {
                    return <div className='response-code-warning'>{t("responseCode" + "-" + responseCode)}</div>
                } else {
                    return <div className='response-code-error'>{t("responseCode" + "-" + responseCode)}</div>
                }
            }
        }),
        WebCache.isUserARG() ?
            currencyColumnDefinition({
                key: 'amountToCustomer', label: t(`${T_PREFIX}transactionsColumn.totalAmountForReport`), category: getCategoryTranslation("transactionDetail"), filterKind: 'number', columnDefinitionKey: 'currency', header: true, mapValue: (rowObject: ITransaction) => {
                    const amountToCustomer = rowObject.amountToCustomer
                    return amountToCustomer ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(amountToCustomer)) : "-"
                }
            }) :
            currencyColumnDefinition({
                key: 'totalAmountForReport', label: t(`${T_PREFIX}transactionsColumn.totalAmountForReport`), category: getCategoryTranslation("transactionDetail"), filterKind: 'number', columnDefinitionKey: 'currency', sortable: true, header: true, mapValue: (rowObject: ITransaction) => {
                    const totalAmountForReport = rowObject.totalAmountForReport
                    // if (rowObject.transactionType === "L"){
                    //     return <div className='d-flex align-items-center' onClick={(event: any) => processAction(event, rowObject)}><div>{ Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(totalAmountForReport))}</div> <div className='fa-solid fa-magnifying-glass text-primary'></div></div>
                    // } else {
                    //     return <div>{ Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(totalAmountForReport))}</div>
                    // }
                    return Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(totalAmountForReport));

                }
            }),
        booleanColumnDefinition({
            key: 'isClosed', label: t(`${T_PREFIX}transactionsColumn.isClosed`), filterKind: 'select', category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                let isClosed: boolean = rowObject.isClosed
                if (isClosed) {
                    return <div className='success-icon fa fa-check-circle-o'></div>
                } else {
                    return "-"
                }
            }
        }),
        textColumnDefinition({
            key: 'id', label: t(`${T_PREFIX}transactionsColumn.id`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const id = rowObject.id
                return id || "-"
            }
        }),
        textColumnDefinition({
            key: 'authorizationCode', label: t(`${T_PREFIX}transactionsColumn.authorizationCode`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const authorizationCode = rowObject.authorizationCode
                return authorizationCode || "-"
            }
        }),
        textColumnDefinition({
            key: 'currency', label: t(`${T_PREFIX}transactionsColumn.currency`), category: getCategoryTranslation("transactionDetail"), filterKind: 'select', header: false, mapValue: (rowObject: ITransaction) => {
                const currency = rowObject.currency
                return t("currency" + "-" + currency)
            }
        }),
        WebCache.isUserARG() ?
            textColumnDefinition({
                key: 'totalAmountForReport', label: t(`${T_PREFIX}transactionsColumn.amount`), category: getCategoryTranslation("transactionDetail"), filterKind: 'number', header: false, mapValue: (rowObject: ITransaction) => {
                    const totalAmountForReport = rowObject.totalAmountForReport
                    return totalAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(totalAmountForReport)) : "-"
                }
            })
            :
            textColumnDefinition({
                key: 'amountToCustomer', label: t(`${T_PREFIX}transactionsColumn.amountToCustomer`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                    const amountToCustomer = rowObject.amountToCustomer
                    return amountToCustomer ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(amountToCustomer)) : "-"
                }
            }),
        textColumnDefinition({
            key: 'merchant', label: t(`${T_PREFIX}transactionsColumn.merchant`), category: getCategoryTranslation("merchantInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const merchant = rowObject.merchant
                return merchant || "-"
            }
        }),
        textColumnDefinition({
            key: 'authTerminal', label: t(`${T_PREFIX}transactionsColumn.authTerminal`), category: getCategoryTranslation("merchantInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const authTerminal = rowObject.authTerminal
                return authTerminal || "-"
            }
        }),
        currencyColumnDefinition({
            key: 'tipAmountForReport', label: t(`${T_PREFIX}transactionsColumn.tipAmountForReport`), category: getCategoryTranslation("transactionDetail"), columnDefinitionKey: 'currency', header: false, mapValue: (rowObject: ITransaction) => {
                const tipAmountForReport = rowObject.tipAmountForReport
                return tipAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(tipAmountForReport)) : "-"
            }
        }),
        currencyColumnDefinition({
            key: 'cashbackAmountForReport', label: t(`${T_PREFIX}transactionsColumn.cashbackAmount`), category: getCategoryTranslation("transactionDetail"), columnDefinitionKey: 'currency', header: false, mapValue: (rowObject: ITransaction) => {
                const cashbackAmountForReport = rowObject.cashbackAmountForReport
                return cashbackAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(cashbackAmountForReport)) : "-"
            }
        }),
        !WebCache.isUserARG() ?
            textColumnDefinition({
                key: 'ticket', label: t(`${T_PREFIX}transactionsColumn.ticket`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                    const ticket = rowObject.ticket
                    return ticket || "-"
                }
            }) : textColumnDefinition({ key: '', label: "", header: false }),
        currencyColumnDefinition({
            key: 'taxAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxAmountForReport`), category: getCategoryTranslation("transactionDetail"), columnDefinitionKey: 'currency', header: false, mapValue: (rowObject: ITransaction) => {
                const taxAmountForReport = rowObject.taxAmountForReport
                return taxAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(taxAmountForReport)) : "-"
            }
        }),
        textColumnDefinition({
            key: 'quotas', label: t(`${T_PREFIX}transactionsColumn.quotas`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const quotas = rowObject.quotas
                return quotas || "-"
            }
        }),
        textColumnDefinition({
            key: 'billNumber', label: t(`${T_PREFIX}transactionsColumn.billNumber`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const billNumber = rowObject.billNumber
                return billNumber ? billNumber.replace(/^0+/, '') : "-"
            }
        }),
        textColumnDefinition({
            key: 'taxRefund', label: t(`${T_PREFIX}transactionsColumn.taxRefund`), category: getCategoryTranslation("transactionDetail"), filterKind: 'select', header: false, mapValue: (rowObjects: ITransaction) => {
                const taxRefund = rowObjects.taxRefund
                if ((taxRefund + "").startsWith("0#")) {
                    return t("taxRefund-0");
                } else {
                    const fields = ("" + taxRefund).split("#");
                    if (fields.length > 0) {
                        const key = fields.length > 1 && fields[1].trim().length > 0
                            ? fields[1].trim()
                            : fields[0].trim();
                        const translationKey = `taxRefund-${key}`;

                        if (translationKey.startsWith("taxRefund-")) {
                            return "-";
                        }
                        return t(translationKey);
                    }
                    return "-";
                }
            }
        }),
        textColumnDefinition({
            key: 'batch', label: t(`${T_PREFIX}transactionsColumn.batch`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const batch = rowObject.batch
                return batch || "-"
            }
        }),
        textColumnDefinition({
            key: 'additionalData', label: t(`${T_PREFIX}transactionsColumn.additionalData`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const additionalData = rowObject.additionalData
                return additionalData || "-"
            }
        }),
        textColumnDefinition({
            key: 'cardNumber', label: t(`${T_PREFIX}transactionsColumn.cardNumber`), category: getCategoryTranslation("paymentInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const cardNumber = rowObject.cardNumber
                return cardNumber || "-"
            }
        }),
        textColumnDefinition({
            key: 'expirationDate', label: t(`${T_PREFIX}transactionsColumn.expirationDate`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const expirationDate = rowObject.expirationDate
                return expirationDate || "-"
            }
        }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'originalTicket', label: t(`${T_PREFIX}transactionsColumn.originalTicket`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const originalTicket = rowObject.originalTicket
                return originalTicket || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'originalDate', label: t(`${T_PREFIX}transactionsColumn.originalDate`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const originalDate = rowObject.originalDate
                return originalDate || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'emvApplicationName', label: t(`${T_PREFIX}transactionsColumn.emvApplicationName`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const emvApplicationName = rowObject.emvApplicationName
                return emvApplicationName || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'emvApplicationId', label: t(`${T_PREFIX}transactionsColumn.emvApplicationId`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const emvApplicationId = rowObject.emvApplicationId
                return emvApplicationId || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'accountType', label: t(`${T_PREFIX}transactionsColumn.accountType`), filterKind: 'select', header: false, category: getCategoryTranslation("transactionDetail"), mapValue: (rowObject: ITransaction) => {
                const accountType = rowObject.accountType;
                if (accountType && accountType.length > 0) {
                    return t("accountType" + "-" + accountType);
                }
                return "-";
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'cardOwnerName', label: t(`${T_PREFIX}transactionsColumn.cardOwnerName`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const cardOwnerName = rowObject.cardOwnerName
                return cardOwnerName || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? currencyColumnDefinition({
            key: 'billAmountForReport', label: t(`${T_PREFIX}transactionsColumn.billAmountForReport`), category: getCategoryTranslation("transactionDetail"), columnDefinitionKey: 'currency', header: false, mapValue: (rowObject: ITransaction) => {
                const billAmountForReport = rowObject.billAmountForReport
                return billAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(billAmountForReport)) : "-"
            }
        }) : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? currencyColumnDefinition({
            key: 'taxableAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxableAmountForReport`), category: getCategoryTranslation("transactionDetail"), columnDefinitionKey: 'currency', header: false, mapValue: (rowObjects: ITransaction) => {
                const taxableAmountForReport = rowObjects.taxableAmountForReport
                return taxableAmountForReport ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObjects.currency == "840" ? "USD" : "UYU" }).format(Number(taxableAmountForReport)) : "-"
            }
        }) : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'originCardType', label: t(`${T_PREFIX}transactionsColumn.originCardType`), category: getCategoryTranslation("paymentInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const originCardType = rowObject.originCardType
                return originCardType || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'initQuestion', label: t(`${T_PREFIX}transactionsColumn.initQuestion`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const initQuestion = rowObject.initQuestion
                return initQuestion || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        WebCache.getCurrentUser()?.countryId == 2 ? textColumnDefinition({
            key: 'terminalGroup', label: t(`${T_PREFIX}transactionsColumn.terminalGroup`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const terminalGroup = rowObject.terminalGroup
                return terminalGroup || "-"
            }
        })
            : textColumnDefinition({ key: '', label: "", header: false }),
        textColumnDefinition({ key: 'clientDocument', label: WebCache.getCurrentUser()?.countryId == 2 ? t(`${T_PREFIX}transactionsColumn.clientDocumentUy`) : t(`${T_PREFIX}transactionsColumn.clientDocumentArg`), category: getCategoryTranslation("transactionDetail"), header: false }),
        textColumnDefinition({
            key: 'address', label: t(`${T_PREFIX}transactionsColumn.address`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const address = rowObject.address
                return address || "-"
            }
        }),
        textColumnDefinition({
            key: 'location', label: t(`${T_PREFIX}transactionsColumn.location`), category: getCategoryTranslation("merchantInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const location = rowObject.location
                return location || "-"
            }
        }),
        textColumnDefinition({ key: 'department', label: WebCache.getCurrentUser()?.countryId == 2 ? t(`${T_PREFIX}transactionsColumn.departamentUy`) : t(`${T_PREFIX}transactionsColumn.departamentArg`), category: getCategoryTranslation("transactionDetail"), header: false }),
        textColumnDefinition({
            key: 'wholesalerName', label: t(`${T_PREFIX}transactionsColumn.wholesalerName`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const wholesalerName = rowObject.wholesalerName
                return wholesalerName || "-"
            }
        }),
        textColumnDefinition({
            key: 'specialData', label: t(`${T_PREFIX}transactionsColumn.specialData`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const specialData = rowObject.specialData
                return specialData || "-"
            }
        }),
        textColumnDefinition({
            key: 'userId', label: t(`${T_PREFIX}transactionsColumn.userId`), category: getCategoryTranslation("paymentInfo"), header: false, mapValue: (rowObject: ITransaction) => {
                const userId = rowObject.userId
                return userId || "-"
            }
        }),
        WebCache.getCurrentUser()?.countryId == 1 ? textColumnDefinition({
            key: 'muId', label: t(`${T_PREFIX}transactionsColumn.muId`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const muId = rowObject.muId
                return muId || "-"
            }
        }) :
            textColumnDefinition({ key: '', label: '' }),
        WebCache.getCurrentUser()?.countryId == 1 ? textColumnDefinition({
            key: 'refundBalance', label: t(`${T_PREFIX}transactionsColumn.refundBalance`), category: getCategoryTranslation("transactionDetail"), header: false, mapValue: (rowObject: ITransaction) => {
                const refundBalance = rowObject.refundBalance
                return refundBalance ? Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(refundBalance)) : "-"
            }
        }) :
            textColumnDefinition({ key: '', label: '' })
    ];
};
